import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../utils/auth";
import { RefreshCw, CheckCircle, XCircle } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import UserProfileMenu from "./UserProfileMenu";

const ROLE_LABELS = {
  user: "Enterprise User",
  support_agent: "Support Agent",
  agent: "Agent",
};

const AdminPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/admin/pending-users");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to fetch pending users.");
      }
      const data = await res.json();
      const users = data.pending_users || [];
      setPendingUsers(users);
    } catch (err) {
      setError(err.message || "Failed to fetch pending users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleAction = async (username, action) => {
    setActionLoading((prev) => ({ ...prev, [username]: action }));
    setError("");
    setSuccessMsg("");
    try {
      const endpoint =
        action === "approve"
          ? "/api/admin/approve-user"
          : "/api/admin/reject-user";
      const user = pendingUsers.find((u) => u.username === username);
      const body =
        action === "approve"
          ? { username, approved_roles: user?.roles || [user?.role].filter(Boolean) }
          : { username };
      const res = await authFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to ${action} user.`);
      }
      const data = await res.json();
      setSuccessMsg(data.message || `User ${action}d successfully.`);
      setPendingUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (err) {
      setError(err.message || `Failed to ${action} user.`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [username]: null }));
    }
  };

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main content */}
      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        <div className="w-full">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">
                Pending Signups
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Approve or reject new user signup requests.
              </p>
            </div>
            <button
              onClick={fetchPending}
              disabled={loading}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {successMsg}
            </div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              No pending signup requests.
            </div>
          ) : (
            <div className="border border-[#EBECE6] rounded-xl overflow-hidden bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f6f6f1] text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">Username</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Requested Roles</th>
                    <th className="px-4 py-3 font-semibold">Signed Up</th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user) => (
                    <tr
                      key={user.username}
                      className="border-t border-[#EBECE6] hover:bg-[#fafaf8] transition"
                    >
                      <td className="px-4 py-3 font-medium text-[#253439]">
                        {user.username}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {user.email || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {(user.roles || [user.role]).filter(Boolean).map((r) => (
                            <span
                              key={r}
                              className="w-fit px-2 py-0.5 rounded text-xs font-semibold border bg-cyan-50 text-cyan-700 border-cyan-300"
                            >
                              {ROLE_LABELS[r] || r}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {user.created
                          ? new Date(user.created).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() =>
                              handleAction(user.username, "approve")
                            }
                            disabled={!!actionLoading[user.username]}
                            className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50"
                          >
                            <CheckCircle size={14} />
                            {actionLoading[user.username] === "approve"
                              ? "…"
                              : "Approve"}
                          </button>
                          <button
                            onClick={() =>
                              handleAction(user.username, "reject")
                            }
                            disabled={!!actionLoading[user.username]}
                            className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50"
                          >
                            <XCircle size={14} />
                            {actionLoading[user.username] === "reject"
                              ? "…"
                              : "Reject"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
